import { useEffect, useState } from 'react';
import { nanoid } from 'nanoid';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangleIcon, Gamepad2Icon, LinkIcon, RefreshCwIcon, ServerIcon, ShieldCheckIcon } from 'lucide-react';
import { formatFcAdminError, formatFcAdminNotice, useFcAdminAction, useFcAdminStatus } from '@/hooks/fcAdmin';
import { fcAdminStatusTransientCode } from '@/lib/fcAdminAccess';

export default function FcAdminStatusBar() {
    const { status, error, loading, authorized, mutationReady, can, accessRevision, refresh } = useFcAdminStatus();
    const openInGame = useFcAdminAction('client.open');
    const [opening, setOpening] = useState(false);
    const [openError, setOpenError] = useState<string>();
    const discordLinked = status?.discordLinked === true;
    const ready = mutationReady;
    const stateCode = fcAdminStatusTransientCode(status) ?? status?.authorizationState;
    const canOpenInGame = authorized && status?.actor?.inGameConnected === true && can('client.open');

    useEffect(() => {
        setOpening(false);
        setOpenError(undefined);
    }, [accessRevision]);

    const handleOpenInGame = async () => {
        if (!canOpenInGame || !ready || opening) return;
        setOpening(true);
        setOpenError(undefined);
        const response = await openInGame({
            payload: {},
            idempotencyKey: `client.open:${nanoid()}`,
        });
        if (!response) setOpenError('Die Ingame-Sitzung hat nicht geantwortet.');
        else if (!response.ok) setOpenError(formatFcAdminNotice(response));
        setOpening(false);
    };

    return (
        <div className="flex flex-wrap items-center gap-2 border-b border-fc-stroke bg-fc-glass px-4 py-2.5">
            <Badge variant={ready ? 'success' : 'warning'} className="gap-1.5">
                {ready ? <ShieldCheckIcon className="size-3.5" /> : <AlertTriangleIcon className="size-3.5" />}
                {ready ? 'FC-Service bereit' : 'FC-Service eingeschränkt'}
            </Badge>
            <Badge variant={discordLinked ? 'default' : 'secondary'} className="gap-1.5">
                <LinkIcon className="size-3.5" />
                {discordLinked ? 'Discord verknüpft' : 'Discord nicht verknüpft'}
            </Badge>
            <Badge variant={status?.gameServerOnline ? 'success' : 'destructive'} className="gap-1.5">
                <ServerIcon className="size-3.5" />
                {status?.gameServerOnline ? 'Gameserver online' : 'Gameserver offline'}
            </Badge>
            {authorized && status?.actor?.role && <Badge variant="default">{status.actor.role}</Badge>}
            {stateCode && stateCode !== 'ready' && stateCode !== 'authorized' && (
                <Badge variant="warning">{formatFcAdminError(stateCode)}</Badge>
            )}
            {typeof status?.retryAfterMs === 'number' && status.retryAfterMs > 0 && (
                <Badge variant="warning">Erneut in {Math.ceil(status.retryAfterMs / 1000)} Sekunden</Badge>
            )}
            {status?.offlineSnapshot && (
                <Badge variant="secondary">Lesestand {new Date(status.offlineSnapshot.capturedAt).toLocaleTimeString('de-DE')}</Badge>
            )}
            {status?.cachedSnapshot && (
                <Badge variant="secondary">Zwischengespeicherter Stand {new Date(status.cachedSnapshot.capturedAt).toLocaleTimeString('de-DE')}</Badge>
            )}
            {error && <span className="select-text text-[12.5px] text-fc-red-soft">{error}</span>}
            {openError && <span className="select-text text-[12.5px] text-fc-red-soft">{openError}</span>}
            {canOpenInGame && (
                <Button className="ml-auto" size="sm" variant="secondary" disabled={!ready || opening} onClick={() => void handleOpenInGame()}>
                    <Gamepad2Icon className="size-3.5" />
                    {opening ? 'Wird geöffnet …' : 'Im Spiel öffnen'}
                </Button>
            )}
            <Button
                size="sm"
                variant="ghost-muted"
                className={canOpenInGame ? undefined : 'ml-auto'}
                disabled={loading}
                onClick={refresh}
            >
                <RefreshCwIcon className={loading ? 'size-3.5 animate-spin' : 'size-3.5'} />
                Aktualisieren
            </Button>
        </div>
    );
}
